import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Check, ChevronDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import WatchSubstatusBadge from './WatchSubstatusBadge';
import { useUpdateWatchSubstatus } from '@/hooks/maintenance/useUpdateWatchSubstatus';
import { useCanOverrideLifecycle } from '@/hooks/maintenance/useCanOverrideLifecycle';
import { MAINTENANCE_PROJECTS_KEY } from '@/lib/queryKeys/maintenanceKeys';
import {
  LIFECYCLE_WATCH_SUBSTATUS_OPTIONS,
  type LifecycleWatchSubstatus,
} from '@/constants/lifecycleStages';

interface Props {
  projectId: string;
  value: LifecycleWatchSubstatus | null;
  editable?: boolean;
}

const isRlsError = (err: any): boolean => {
  const msg = (err?.message || '').toLowerCase();
  return (
    err?.code === '42501' ||
    msg.includes('row-level security') ||
    msg.includes('permission denied') ||
    msg.includes('insufficient_privilege')
  );
};

export default function WatchSubstatusCell({ projectId, value, editable = true }: Props) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const canEdit = useCanOverrideLifecycle() && editable;
  const mutation = useUpdateWatchSubstatus();

  if (!canEdit) {
    return <WatchSubstatusBadge value={value} />;
  }

  // null treated as 'theo_doi' (default), since no "Chưa set" option
  const effective: LifecycleWatchSubstatus = value ?? 'theo_doi';

  const handlePick = (next: LifecycleWatchSubstatus) => {
    setOpen(false);
    if (next === effective) return;

    // Optimistic patch across both caches (build table + maintenance table)
    const patchCache = (queryKey: any[]) =>
      qc.getQueriesData({ queryKey }).map(([key, data]) => {
        qc.setQueryData(key, (old: any) => {
          if (!old) return old;
          // Shape A: { projects: [...] }
          if (Array.isArray(old?.projects)) {
            return {
              ...old,
              projects: old.projects.map((p: any) =>
                p.id === projectId ? { ...p, watch_substatus: next } : p,
              ),
            };
          }
          // Shape B: array of projects directly
          if (Array.isArray(old)) {
            return old.map((p: any) =>
              p.id === projectId ? { ...p, watch_substatus: next } : p,
            );
          }
          return old;
        });
        return [key, data] as const;
      });

    const snapshots = [
      ...patchCache(['projects-optimized-v2']),
      ...patchCache([MAINTENANCE_PROJECTS_KEY]),
    ];


    mutation.mutate(
      { projectId, value: next },
      {
        onError: (err: any) => {
          // Revert cache snapshot on ANY error (including 42501 RLS)
          snapshots.forEach(([k, d]) => qc.setQueryData(k, d));
          if (isRlsError(err)) {
            toast.error('Bạn không có quyền sửa trạng thái phụ');
          }
          // useUpdateWatchSubstatus already toasts generic errors otherwise
        },
      },
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="cursor-pointer inline-flex items-center gap-1"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? (
            <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Đang lưu…
            </span>
          ) : (
            <>
              <WatchSubstatusBadge value={value} />
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-44 p-1"
        align="start"
        onClick={(e) => e.stopPropagation()}
      >
        {LIFECYCLE_WATCH_SUBSTATUS_OPTIONS.map((opt) => {
          const isCurrent = opt.value === effective;
          return (
            <Button
              key={opt.value}
              variant="ghost"
              size="sm"
              className={cn('w-full justify-start gap-2 h-8', isCurrent && 'font-semibold')}
              onClick={() => handlePick(opt.value as LifecycleWatchSubstatus)}
            >
              <Check className={cn('h-3 w-3', isCurrent ? 'opacity-100' : 'opacity-0')} />
              {opt.label}
            </Button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
